import {
  LinksFunction,
  LoaderFunctionArgs,
  MetaFunction,
} from "@remix-run/node";
import { Link, useLoaderData } from "@remix-run/react";
import { ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import BlogBox from "~/components/blog/BlogBox";
import { getBlog, getBlogs } from "~/api/base.server";
import categories, { CategoryType } from "~/utils/data/categories";
import { localeAttr } from "~/utils/helper";
import i18next from "~/utils/localization/i18next.server";
import Blog from "~/types/Blog";
import ArticleOrCaseStudy from "./ArticleOrCaseStudy";
import Webinar from "./Webinar";
import Report from "./Report";
import { getToast } from "remix-toast";
import EBook from "./EBook";

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  return [
    { title: data?.blog.attributes.title },
    {
      name: "description",
      content: "Blog post on Wearby",
    },
    {
      property: "og:title",
      content: data?.blog.attributes.title,
    },
    {
      property: "og:url",
      content: "https://wearby.app/blog/" + data?.blog.id,
    },
    {
      property: "og:type",
      content: "website",
    },
    {
      property: "og:site_name",
      content: "Wearby",
    },
    {
      property: "og:description",
      content: "Blog post on Wearby",
    },
    {
      property: "og:image",
      content: data?.blog.attributes.image?.data.attributes.url,
    },
  ];
};

export async function loader({ request, params }: LoaderFunctionArgs) {
  if (!params.blogId || isNaN(parseInt(params.blogId))) {
    throw new Error("Invalid blogId");
  }
  const blog = await getBlog(parseInt(params.blogId));
  if (!blog) {
    throw new Response("Blog not found", { status: 404 });
  }

  const blogCategory =
    categories.find((c) =>
      blog.attributes.blog_tags?.data.some(
        (t) => t.attributes.name === c.iName,
      ),
    ) || categories[0];

  let otherWebinars: Blog[] | undefined;
  if (blogCategory.id === CategoryType.Webinars) {
    otherWebinars = (
      await getBlogs({
        filters: {
          blog_tags: { name: { $eq: blogCategory.iName } },
          id: { $ne: blog.id },
        },
        pagination: { limit: 3 },
      })
    )?.data;
  }

  const locale = await i18next.getLocale(request);

  const { toast } = await getToast(request);

  return { blog, locale, blogCategory, otherWebinars, toast };
}

export default function BlogDetail() {
  const { t } = useTranslation("blog");
  const { blog, locale, blogCategory } = useLoaderData<typeof loader>();

  return (
    <div>
      <div className="flex items-center gap-3 text-lg font-bold text-db-550 max-lg:gap-1 max-lg:text-base ">
        <Link to={`/blogs/${blogCategory.id}`}>{t("blogs")}</Link>
        <ChevronRight className="h-5 rtl:rotate-180" />
        <Link to={`/blogs/${blogCategory}`}>
          {localeAttr(
            categories.find((c) => c.id === blogCategory.id)!,
            "name",
            locale,
          )}
        </Link>
        <ChevronRight className="h-5 rtl:rotate-180" />
        <p className="font-normal text-Secondary-Dark-Blue-db-500">
          {localeAttr(blog.attributes, "title", locale)}
        </p>
      </div>
      <div className="mt-8">
        <BlogPage categoryId={blogCategory.id} />
      </div>
    </div>
  );
}

function BlogPage({ categoryId }: { categoryId: number }) {
  switch (categoryId) {
    case CategoryType.Articles:
    case CategoryType.CaseStudies:
      return <ArticleOrCaseStudy />;

    case CategoryType.Webinars:
      return <Webinar />;

    case CategoryType.Reports:
      return <Report />;

    case CategoryType.EBook:
      return <EBook />;

    default:
      return <></>;
  }
}
